/**
 * FP Grid - franchise x exception-type heat matrix.
 *
 * Renders via the shared `DataViewTable` (NOT a fork): domains become
 * colspan group headers, exception types become columns with per-cell heat
 * backgrounds on the shared coral->red ramp, the franchise column is frozen
 * left and the row total frozen right. A non-empty cell is a react-router
 * `<Link>` into that FP + exception type's inspector view (the "action from
 * it" path). Market-segmented + worst-first by default; the backend already
 * returns only franchises with a non-zero total for the chosen metric.
 */
import React, { useMemo, useState } from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import DataViewTable from '../../../../components/shared/DataViewTable/DataViewTable'
import type {
  ExceptionColumnDef,
  ExceptionConfig,
} from '../../../../config/exceptions/types'
import DataFetchWrapper from '../../../../components/inspector/shared/DataFetchWrapper'
import { useDataFetching } from '../../../../hooks/useDataFetching'
import {
  dataExceptionsApi,
  type GridMetric,
  type GridResponse,
} from '../../../../services/admin/dataExceptionsService'
import { dqHeatBackground, dqHeatText } from '../../../../theme/dqHeatRamp'
import {
  dataDomainToObjectType,
  generateExceptionUrl,
  generateInspectorUrl,
} from '../../../../utils/urls'
import { poolwerxColors } from '../../../../theme/colors'
import MarketChip from '../components/MarketChip'
import { useDataExceptionsCtx } from '../DataExceptionsContext'
import { useSearchParams } from 'react-router-dom'

const METRICS: { value: GridMetric; label: string }[] = [
  { value: 'needs_review', label: 'Needs review' },
  { value: 'open', label: 'All open' },
  { value: 'blocked', label: 'Blocked' },
]

const VerticalHeader: React.FC<{ name: string; highlighted: boolean }> = ({
  name,
  highlighted,
}) => (
  <Tooltip title={name}>
    <Box
      sx={{
        writingMode: 'vertical-rl',
        transform: 'rotate(180deg)',
        maxHeight: 150,
        mx: 'auto',
        fontSize: '0.72rem',
        fontWeight: highlighted ? 700 : 500,
        color: highlighted ? poolwerxColors.teal : 'text.secondary',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {name}
    </Box>
  </Tooltip>
)

const FpGrid: React.FC = () => {
  const { market, refreshKey } = useDataExceptionsCtx()
  const [searchParams] = useSearchParams()
  const highlightCode = searchParams.get('type') || ''
  const [metric, setMetric] = useState<GridMetric>('needs_review')

  const { data, loading, error } = useDataFetching<GridResponse>({
    fetchFn: () =>
      dataExceptionsApi.getGrid({ metric, market: market || undefined }),
    dependencies: [metric, market, refreshKey],
  })

  // Flatten {counts:{code:n}} into row[code]=n so the table's column keys
  // resolve directly; carry the row total for the frozen total column.
  const rows = useMemo(
    () =>
      (data?.rows || []).map((r) => ({
        fp_code: r.fp_code,
        franchise_name: r.franchise_name,
        market: r.market,
        total: r.total,
        ...r.counts,
      })),
    [data],
  )

  // Heat scales against the single hottest cell so the ramp uses its full
  // range regardless of the metric's absolute magnitude.
  const heatMax = useMemo(
    () => Math.max(1, ...Object.values(data?.totals || {}), ...rows.map((r) => r.total)),
    [data, rows],
  )

  const config: ExceptionConfig = useMemo(() => {
    const typeCols: ExceptionColumnDef[] = (data?.exception_types || []).map(
      (t) => ({
        key: t.code,
        label: t.name,
        align: 'center',
        group: t.domain,
        renderHeader: () => (
          <VerticalHeader name={t.name} highlighted={t.code === highlightCode} />
        ),
        getCellStyle: (value: number) => ({
          backgroundColor: dqHeatBackground(value || 0, heatMax),
          color: dqHeatText(value || 0, heatMax),
          fontWeight: value ? 600 : 400,
          ...(t.code === highlightCode
            ? { outline: `2px solid ${poolwerxColors.teal}`, outlineOffset: '-2px' }
            : {}),
        }),
        render: (value: number, row: Record<string, any>) => {
          if (!value) return '-'
          const objectType = dataDomainToObjectType(t.domain)
          const to = objectType
            ? generateExceptionUrl(row.fp_code, objectType, t.code, row.fp_id)
            : generateInspectorUrl(row.fp_code, { id: row.fp_id, subpath: 'exceptions' })
          return (
            <RouterLink to={to} style={{ color: 'inherit', fontWeight: 600 }}>
              {value.toLocaleString()}
            </RouterLink>
          )
        },
      }),
    )

    const columns: ExceptionColumnDef[] = [
      {
        key: 'franchise_name',
        label: 'Franchise',
        freeze: 'left',
        render: (_v, row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MarketChip market={row.market} />
            <RouterLink
              to={generateInspectorUrl(row.fp_code, { id: row.fp_id, subpath: 'exceptions' })}
              style={{ color: poolwerxColors.teal, fontWeight: 600 }}
            >
              {row.franchise_name || row.fp_code}
            </RouterLink>
          </Box>
        ),
      },
      ...typeCols,
      {
        key: 'total',
        label: 'Total',
        align: 'right',
        freeze: 'right',
        render: (value: number) => (
          <strong>{(value || 0).toLocaleString()}</strong>
        ),
      },
    ]

    return {
      codes: [],
      columns,
      columnGroups: (data?.domains || []).map((d) => ({
        id: d.key,
        label: d.label,
        separatorAfter: true,
      })),
      defaultSortColumn: 'total',
      defaultSortDirection: 'desc',
      defaultSortKeys: ['total'],
      complexity: 'simple',
      rowKeyField: 'fp_code',
      clientDefaultPageSize: 200,
      filters: [
        {
          key: 'fp',
          type: 'search',
          label: 'Franchise',
          placeholder: 'Filter franchises',
          predicate: (row, term) => {
            const t = term.toLowerCase()
            return (
              String(row.franchise_name || '').toLowerCase().includes(t) ||
              String(row.fp_code || '').toLowerCase().includes(t)
            )
          },
        },
      ],
    }
  }, [data, heatMax, highlightCode])

  return (
    <Box>
      <Box
        sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(_e, v) => v && setMetric(v)}
        >
          {METRICS.map((m) => (
            <ToggleButton key={m.value} value={m.value}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {data && (
          <Typography variant="caption" color="text.secondary">
            {data.rows.length.toLocaleString()} franchises ·{' '}
            {data.grand_total.toLocaleString()} {metric.replace('_', ' ')} · worst first
          </Typography>
        )}
      </Box>

      <DataFetchWrapper
        loading={loading}
        error={error}
        data={rows}
        emptyTitle="No exceptions in this slice"
        emptyDescription="No franchise has an exception for the selected metric and market."
      >
        <DataViewTable
          config={config}
          data={rows}
          variant="platform"
          isLoading={loading}
        />
      </DataFetchWrapper>
    </Box>
  )
}

export default FpGrid
